import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardInputConfig {
  onKeyPress: (key: string) => void;
  enabled: boolean;
  preventDefaultKeys?: string[];
  ignoreKeys?: string[];
}

interface KeyboardState {
  lastKeyPressed: string | null;
  keyPressCount: number;
  isListening: boolean;
}

export const useKeyboardInput = (config: KeyboardInputConfig) => {
  const stateRef = useRef<KeyboardState>({
    lastKeyPressed: null,
    keyPressCount: 0,
    isListening: false
  });

  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Handles keydown events
   */
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const { onKeyPress, enabled, preventDefaultKeys = [], ignoreKeys = [] } = configRef.current;
    
    if (!enabled) return;

    // Get the pressed key
    let key = event.key;
    
    // Handle special keys
    if (key === ' ') {
      key = ' '; // Keep space as space for processing
    } else if (key === 'Enter') {
      key = '\n';
    } else if (key === 'Tab') {
      key = '\t';
    }

    // Ignore certain keys
    if (ignoreKeys.includes(key) || ignoreKeys.includes(event.code)) {
      return;
    }

    // Ignore modifier keys and special keys
    const modifierKeys = [
      'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'NumLock', 'ScrollLock',
      'Escape', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'Delete',
      'Backspace' // We don't use backspace in this training system
    ];

    if (modifierKeys.includes(key)) {
      return;
    }

    // Prevent default behavior for specified keys
    if (preventDefaultKeys.includes(key) || preventDefaultKeys.includes('all')) {
      event.preventDefault();
    }

    // Ignore if modifier keys are held down (except Shift for capitals)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    // Handle Shift key combinations for capital letters
    if (event.shiftKey && key.length === 1 && /[a-z]/.test(key)) {
      key = key.toUpperCase();
    }

    // Update state
    stateRef.current = {
      lastKeyPressed: key,
      keyPressCount: stateRef.current.keyPressCount + 1,
      isListening: true
    };

    // Call the onKeyPress callback
    onKeyPress(key);
  }, []);

  /**
   * Handles keyup events (for cleanup if needed)
   */
  const handleKeyUp = useCallback((_event: KeyboardEvent) => {
    // Currently not used, but available for future enhancements
  }, []);

  /**
   * Sets up event listeners
   */
  useEffect(() => {
    if (!configRef.current.enabled) {
      return;
    }

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // Update listening state
    stateRef.current.isListening = true;

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      stateRef.current.isListening = false;
    };
  }, [handleKeyDown, handleKeyUp, config.enabled]);

  /**
   * Manually trigger a key press (useful for testing)
   */
  const simulateKeyPress = useCallback((key: string) => {
    if (configRef.current.enabled) {
      configRef.current.onKeyPress(key);
      stateRef.current = {
        lastKeyPressed: key,
        keyPressCount: stateRef.current.keyPressCount + 1,
        isListening: stateRef.current.isListening
      };
    }
  }, []);

  /**
   * Gets current keyboard state
   */
  const getKeyboardState = useCallback((): KeyboardState => {
    return { ...stateRef.current };
  }, []);

  /**
   * Resets keyboard state
   */
  const resetKeyboardState = useCallback(() => {
    stateRef.current = {
      lastKeyPressed: null,
      keyPressCount: 0,
      isListening: stateRef.current.isListening
    };
  }, []);

  /**
   * Focus management for better UX
   */
  const focusKeyboardCapture = useCallback(() => {
    // Ensure the document has focus for keyboard capture
    if (document.activeElement !== document.body) {
      document.body.focus();
    }
  }, []);

  return {
    simulateKeyPress,
    getKeyboardState,
    resetKeyboardState,
    focusKeyboardCapture,
    isListening: stateRef.current.isListening,
    lastKeyPressed: stateRef.current.lastKeyPressed,
    keyPressCount: stateRef.current.keyPressCount
  };
};

/**
 * Hook for capturing specific key combinations
 */
export const useKeyboardShortcuts = (shortcuts: Record<string, () => void>) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Build key combination string
    const modifiers = [];
    if (event.ctrlKey) modifiers.push('Ctrl');
    if (event.altKey) modifiers.push('Alt');
    if (event.shiftKey) modifiers.push('Shift');
    if (event.metaKey) modifiers.push('Meta');
    
    const keyCombo = [...modifiers, event.key].join('+');
    
    // Check if this combination has a handler
    if (shortcuts[keyCombo]) {
      event.preventDefault();
      shortcuts[keyCombo]();
    }
  }, [shortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

/**
 * Utility function to check if a key is a valid typing character
 */
export const isValidTypingKey = (key: string): boolean => {
  // Letters (a-z, A-Z)
  if (/^[a-zA-Z]$/.test(key)) return true;
  
  // Numbers (0-9)
  if (/^[0-9]$/.test(key)) return true;
  
  // Space
  if (key === ' ') return true;
  
  // Common punctuation (extend as needed)
  const punctuation = ['.', ',', ';', ':', '!', '?', "'", '"', '-', '_'];
  if (punctuation.includes(key)) return true;
  
  return false;
};

/**
 * Utility function to normalize keys for consistent processing
 */
export const normalizeKey = (key: string): string => {
  // Convert space to underscore for display
  if (key === ' ') return '_';
  
  // Ensure uppercase for letters
  if (/^[a-z]$/.test(key)) return key.toUpperCase();
  
  // Return as-is for other keys
  return key;
};

/**
 * Hook for tracking typing rhythm and patterns
 */
export const useTypingRhythm = () => {
  const keystrokeTimings = useRef<number[]>([]);
  const lastKeystrokeTime = useRef<number>(0);

  const recordKeystroke = useCallback(() => {
    const now = Date.now();
    if (lastKeystrokeTime.current > 0) {
      const interval = now - lastKeystrokeTime.current;
      keystrokeTimings.current.push(interval);
      
      // Keep only last 50 keystrokes for analysis
      if (keystrokeTimings.current.length > 50) {
        keystrokeTimings.current.shift();
      }
    }
    lastKeystrokeTime.current = now;
  }, []);

  const getTypingRhythm = useCallback(() => {
    if (keystrokeTimings.current.length < 2) return null;
    
    const average = keystrokeTimings.current.reduce((a, b) => a + b, 0) / keystrokeTimings.current.length;
    const variance = keystrokeTimings.current.reduce((acc, timing) => {
      return acc + Math.pow(timing - average, 2);
    }, 0) / keystrokeTimings.current.length;
    
    return {
      averageInterval: average,
      consistency: Math.sqrt(variance), // Standard deviation
      rhythmScore: Math.max(0, 100 - (Math.sqrt(variance) / average) * 100) // 0-100 score
    };
  }, []);

  const resetRhythm = useCallback(() => {
    keystrokeTimings.current = [];
    lastKeystrokeTime.current = 0;
  }, []);

  return {
    recordKeystroke,
    getTypingRhythm,
    resetRhythm
  };
};
